import { SearchDto, SortDirection, SortModel } from "../utils/lib";
import { VfxDialogProps } from "../vfx/dialog/vfxDialogProps";

export const GRID_ACTION_TYPES = {
    NEW: "new",
    EDIT: "edit",
    VIEW: "view",
    VIEW_DELTE: "view_delete",
    HIDDEN: "hidden",
    COPY: "copy",
    DOUBLE_CLICK: "double-click",
    PERMISSION: "permission",
    NEW_SIBLING: "new-sibling",
    DEACTIVATE: "deactivate",
    DELETE: "delete",
    ADD_FAVORITE: "favourite",
    FAVORITED: "favorited",
    APPROVE: "approve",
    APPROVED: "approved",
    REJECT: "reject",
    REJECTED: "rejected",
    NOTES: "notes",
    VIEW_REPLY: "view-replies",
    SEND_REPLY: "send-reply",
    UNLOCK: "unlock",
    RESET_PASSWORD: "reset-password",
    RESEND_CONFIRMATION_MAIL: "resend-confirmation-email",
    TAG: "tag",
} as const;

export type GridActionType =
    (typeof GRID_ACTION_TYPES)[keyof typeof GRID_ACTION_TYPES];

export enum Module {
    None = "00000000-0000-0000-0000-000000000000",
    Admin = "052b3ae3-c710-f011-8ef5-00155d112802",
    Account = "a0175df3-c710-f011-8ef5-00155d112802",
    Communication = "3e2bbafd-c710-f011-8ef5-00155d112802",
    Community_Resource = "be647a09-c810-f011-8ef5-00155d112802",
    PopHub = "014e9b17-c810-f011-8ef5-00155d112802",
    System = "75901424-c810-f011-8ef5-00155d112802",
    Questionnaire = "46182104-6311-f011-8ef5-00155d112802",
}

export enum LocalStorage {
    communityId = "communityId",
}

export const LookupModuleRouteMap: Record<Module, string> = {
    [Module.None]: "",
    [Module.Account]: "account",
    [Module.Admin]: "admin",
    [Module.Communication]: "communication",
    [Module.Community_Resource]: "communityresource",
    [Module.PopHub]: "pophub",
    [Module.System]: "system",
    [Module.Questionnaire]: "questionnaire",
};

export const SettingModuleRouteMap: Record<Module, string> = {
    [Module.None]: "",
    [Module.Account]: "account",
    [Module.Admin]: "admin",
    [Module.Communication]: "communication",
    [Module.Community_Resource]: "communityResource",
    [Module.PopHub]: "popHub",
    [Module.System]: "system",
    [Module.Questionnaire]: "questionnaire",
};

export const sortBy: SortModel = {
    field: "name",
    direction: SortDirection.Ascending,
};

export const defaultSearchQuery: SearchDto = {
    filters: [{ field: "isDeleted", value: false }],
    pageSize: 10,
    pageNumber: 1,
    sorts: [sortBy],
};

export const defaultControlledSearchQuery: SearchDto = {
    filters: [{ field: "isDeleted", value: false }],
    pageSize: 10,
    pageNumber: 1,
    sortBy,
    sorts: [sortBy],
};

export class ConfirmationDialog {
    protected DefaultDelete: VfxDialogProps = {
        contentText: "By taking this action, {{{Name}}} will be permanently deleted from the platform. This action is irreversible. Are you sure you want to continue?",
        contentTitle: "Delete  {{{Name}}}",
        closeText: "No, Go Back",
        closeButtonClass: "primary",
        confirmText: "Yes, Continue",
        confirmButtonClass: "outline-danger",
        open: false,
        onClose: () => { },
        onConfirm: () => { },
    };

    protected DefaultDeactivate: VfxDialogProps = {
        contentText: "By taking this action, {{{Name}}} will be permanently deactivated from the platform. This action is irreversible. Are you sure you want to continue?",
        contentTitle: "Deactivate  {{{Name}}}",
        closeText: "No, Go Back",
        closeButtonClass: "primary",
        confirmText: "Yes, Continue",
        confirmButtonClass: "outline-danger",
        open: false,
        onClose: () => { },
        onConfirm: () => { },
    };

    protected DefaultCancel: VfxDialogProps = {
        contentText: "Are you sure you want to cancel {{{Name}}}?",
        contentTitle: "Cancel",
        closeText: "No, Go Back",
        confirmText: "Yes, Continue",
        open: false,
        onClose: () => { },
        onConfirm: () => { },
    };

    get = ( onConfirm: () => void, modalType: "Delete" | "Deactivate" | "Cancel", replaceValues?: Record<string, string> | undefined ): VfxDialogProps | undefined => {
        let dialogProps: VfxDialogProps | undefined = undefined;

        switch (modalType) {
            case "Cancel": {
                dialogProps = this.DefaultCancel;
                break;
            }
            case "Delete": {
                dialogProps = this.DefaultDelete;
                break;
            }
            case "Deactivate": {
                dialogProps = this.DefaultDeactivate;
                break;
            }
        }

        if (dialogProps != undefined) {
            dialogProps.onConfirm = onConfirm;
            dialogProps = this.setContentText(dialogProps, replaceValues);
        }
        return dialogProps;
    };

    setContentText = (
        dialogProps: VfxDialogProps | undefined,
        replaceValues?: Record<string, string> | undefined
    ): VfxDialogProps | undefined => {
        if (dialogProps != undefined) {
            if (replaceValues !== undefined) {
                for (const key in replaceValues) {
                    const data = replaceValues[key];
                    dialogProps.contentText = dialogProps.contentText?.replace(`{{{${key}}}}`, data);
                    dialogProps.contentTitle = dialogProps.contentTitle?.replace(`{{{${key}}}}`, data);
                }
            }
        }
        return dialogProps;
    };
}

export enum QueryKeyTypes {
    None,
    Grid,
    Get,
    GetAll,
    Lookup,
}

export type SetState<T> = React.Dispatch<React.SetStateAction<T>>;
